/**
 * Human-readable label caption derived from a QR_Token.
 *
 * A printed label's QR still encodes the full opaque `tcvs:<type>:<id>` token,
 * but the long id is hard for a picker to read or say aloud. So beneath the QR
 * we print a short, friendly caption like `Batch: 3FM8E1` or `Box: B5H55C` —
 * the container TYPE plus a short code. The QR remains authoritative for
 * scanning; the caption is a visual aid only (its short code is the tail of the
 * id and is therefore not guaranteed unique, but a scan can never resolve to the
 * wrong item).
 *
 * For a CARD, the caller passes the card's human-friendly `shortCode` (the real
 * operator-facing id), which is shown verbatim instead of a slice of the UUID.
 *
 * Pure and DOM-free so both the on-screen `LabelTile` and the generated PDF
 * (`label-pdf.ts`) share one definition and stay consistent.
 */

/** How many trailing token characters form the short visual code (containers). */
export const LABEL_CODE_LENGTH = 6;

const TYPE_LABELS: Record<string, string> = {
  rack: 'Rack',
  shelf: 'Shelf',
  box: 'Box',
  row: 'Row',
  batch: 'Batch',
  card: 'Card',
};

/** The token's `<type>` segment (`tcvs:<type>:<id>`), lowercased, or undefined. */
function tokenType(token: string): string | undefined {
  const parts = token.split(':');
  return parts.length >= 3 ? parts[1]?.toLowerCase() : undefined;
}

/** The opaque id body after the last colon (the ULID / cardId), or the whole value. */
function idBody(token: string): string {
  const lastColon = token.lastIndexOf(':');
  return lastColon === -1 ? token : token.slice(lastColon + 1);
}

export interface LabelCaption {
  /** Display type, e.g. `Batch`, `Box`, `Shelf`, `Card`. */
  typeLabel: string;
  /** The short human code shown after the type. */
  code: string;
  /** `"<typeLabel>: <code>"`, the one-line caption for the label. */
  text: string;
}

/**
 * Build the label caption for a QR token. For a card, pass the card's
 * `shortCode` to show it verbatim; otherwise the caption is the type plus the
 * last {@link LABEL_CODE_LENGTH} characters of the opaque id.
 */
export function labelCaption(token: string, shortCode?: string): LabelCaption {
  const type = tokenType(token);
  const typeLabel = (type !== undefined && TYPE_LABELS[type]) || 'Item';

  let code: string;
  if (type === 'card' && shortCode !== undefined && shortCode !== '') {
    // Cards have a real human-friendly short code — use it, not a UUID slice.
    code = shortCode;
  } else {
    const body = idBody(token);
    code =
      body.length > LABEL_CODE_LENGTH ? body.slice(body.length - LABEL_CODE_LENGTH) : body;
  }
  return { typeLabel, code, text: `${typeLabel}: ${code}` };
}
